import { useNavigate } from 'react-router-dom';
import { Flame } from 'lucide-react';
import type { DailyStreakEntry } from '../api/puzzles.ts';
import { useAuth } from '../hooks/useAuth.ts';
import { useDailyStreak } from '../hooks/useDailyStreak.ts';


function TextContent({ flameColor, textColor, subColor, s }: {
  flameColor: string; textColor: string; subColor: string; s: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Flame size={16} color={flameColor} strokeWidth={2.5} />
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: textColor, lineHeight: 1 }}>
          {s}
        </span>
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: subColor }}>
        streak
      </span>
    </div>
  );
}

function StreakCard({ streak }: { streak: number }) {
  const t = Math.min(streak, 10) / 10;

  // "/" diagonal: orange fills from bottom-right, rising as streak increases.
  // center: 120% (invisible at t=0) → -25% (fully covered at t=1).
  const tilt = 18;
  const center = 120 - t * 145;
  const TL = center + tilt; // left edge y%
  const TR = center - tilt; // right edge y%
  const lowerClip = `polygon(0% ${TL}%, 100% ${TR}%, 100% 100%, 0% 100%)`;
  const upperClip = `polygon(0% 0%, 100% 0%, 100% ${TR}%, 0% ${TL}%)`;

  // Shared flex-center style for both text layers.
  const layerStyle: React.CSSProperties = {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div
        className="card"
        style={{
          width: '100%', height: 60,

          borderRadius: 'var(--radius-card)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Orange fill */}
        {streak > 0 && <div style={{ position: 'absolute', inset: 0, background: 'var(--streak-fire)', clipPath: lowerClip }} />}

        {/* Dark text — clipped to the surface region above the diagonal */}
        <div style={{ ...layerStyle, clipPath: streak > 0 ? upperClip : undefined }}>
          <TextContent flameColor={streak === 0 ? 'var(--muted)' : 'var(--ink)'} textColor={streak === 0 ? 'var(--muted)' : 'var(--ink)'} subColor="var(--muted)" s={streak} />
        </div>

        {/* White text — clipped to the orange region below the diagonal */}
        {streak > 0 && (
          <div style={{ ...layerStyle, clipPath: lowerClip }}>
            <TextContent flameColor="white" textColor="white" subColor="white" s={streak} />
          </div>
        )}
      </div>
      <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'transparent' }}>·</span>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
}

function StreakCell({ entry }: { entry: DailyStreakEntry }) {
  const navigate = useNavigate();
  const isBest = entry.userMoves != null && entry.overallBest != null && entry.userMoves === entry.overallBest;
  const solved = entry.userMoves != null;
  const accent = isBest ? 'var(--best)' : solved ? 'var(--solved)' : null;
  const textColor = isBest ? 'var(--best)' : solved ? 'var(--solved)' : 'var(--muted)';

  function handleClick() {
    navigate(`/puzzles/solve/${entry.puzzleId}`, {
      state: {
        puzzle: {
          id: entry.puzzleId,
          name: entry.name,
          encoded: entry.encoded,
          gridSize: entry.gridSize,
          numTargets: entry.numTargets ?? undefined,
          difficulty: entry.difficulty ?? undefined,
          minMoves: entry.par,
        },
        apiMeta: {
          puzzleId: entry.puzzleId,
          likeCount: 0,
          userBest: entry.userMoves,
          communityBest: entry.overallBest,
        },
      },
    });
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <button
        onClick={handleClick}
        className="card"
        style={{
          width: '100%', height: 60,

          borderRadius: 'var(--radius-card)',
          border: accent ? `2px solid ${accent}` : undefined,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 2,
          cursor: 'pointer',
          transition: 'transform 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
        onMouseLeave={e => (e.currentTarget.style.transform = '')}
      >
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500,
          color: textColor, lineHeight: 1,
        }}>
          {entry.userMoves ?? '—'}
        </span>
        {solved && (
          <>
            <div style={{ width: '60%', height: 1, background: 'var(--muted)', margin: '2px 0' }} />
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'var(--muted)',
            }}>
              {isBest ? 'best' : 'solved'}
            </span>
          </>
        )}
      </button>
      <span style={{
        fontSize: 10, fontFamily: 'var(--font-mono)',
        color: 'var(--muted)', textAlign: 'center',
      }}>
        {formatDate(entry.date)}
      </span>
    </div>
  );
}

const PLACEHOLDER_DAYS = ['Mon 19', 'Tue 20', 'Wed 21', 'Thu 22'];

function PlaceholderRow() {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      {PLACEHOLDER_DAYS.map((label) => (
        <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <div className="card" style={{
            width: '100%', height: 60,
  
            borderRadius: 'var(--radius-card)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--muted)' }}>—</span>
          </div>
          <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--muted)' }}>{label}</span>
        </div>
      ))}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div className="card" style={{
          width: '100%', height: 60,

          borderRadius: 'var(--radius-card)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 2,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Flame size={16} color="var(--muted)" strokeWidth={2.5} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--muted)', lineHeight: 1 }}>0</span>
          </div>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>streak</span>
        </div>
        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'transparent' }}>·</span>
      </div>
    </div>
  );
}

export default function DailyStreak() {
  const { isAuthenticated, login } = useAuth();
  const { entries, streak, loading } = useDailyStreak();

  if (loading) return null;

  const showBlurred = !isAuthenticated;

  // API returns newest-first; reverse so oldest is on the left
  const ordered = [...entries].reverse();

  return (
    <div className="anim-stagger-up" style={{ marginBottom: 40, '--anim-delay': '160ms' } as React.CSSProperties}>
      <h2 style={{
        fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 500,
        margin: '0 0 14px', color: 'var(--ink)',
      }}>
        Daily Streak
      </h2>
      <div style={{ position: 'relative' }}>
        {showBlurred ? (
          <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none' }}>
            <PlaceholderRow />
          </div>
        ) : (
          entries.length > 0 && (
            <div style={{ display: 'flex', gap: 10 }}>
              {ordered.map((entry) => (
                <StreakCell key={entry.puzzleId} entry={entry} />
              ))}
              <StreakCard streak={streak} />
            </div>
          )
        )}
        {showBlurred && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <button className="btn btn-accent" style={{ boxShadow: 'var(--shadow-card)' }} onClick={login}>
              Log in to see your streak
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
